/**
 * Wakaana Express — Web Push utility
 * Gère l'abonnement/désabonnement aux notifications push.
 */

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

/** Convertit une clé VAPID base64url en Uint8Array pour le PushManager */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) output[i] = rawData.charCodeAt(i);
  return output;
}

/**
 * Demande la permission et enregistre l'abonnement push en base.
 * @param {object} supabase  - client Supabase
 * @param {object} opts      - { role, driverId?, store? }
 */
export async function subscribePush(supabase, { role, driverId = null, store = null }) {
  if (!VAPID_PUBLIC_KEY) {
    console.warn('REACT_APP_VAPID_PUBLIC_KEY non défini — push désactivé');
    return null;
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('Web Push non supporté sur ce navigateur');
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const reg = await navigator.serviceWorker.ready;

    // Récupérer ou créer l'abonnement
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }

    const subJson = sub.toJSON();

    // Upsert en base (clé unique = endpoint)
    const { error } = await supabase.from('push_subscriptions').upsert(
      {
        role,
        driver_id: driverId ? String(driverId) : null,
        store: store || null,
        endpoint: subJson.endpoint,
        subscription: subJson,
      },
      { onConflict: 'endpoint' }
    );

    if (error) console.error('subscribePush upsert error:', error.message);
    return sub;
  } catch (err) {
    console.error('subscribePush error:', err);
    return null;
  }
}

/**
 * Supprime l'abonnement push du navigateur et de la base.
 * @param {object} supabase - client Supabase
 */
export async function unsubscribePush(supabase) {
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint);
  } catch (err) {
    console.error('unsubscribePush error:', err);
  }
}
